import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface ComptesRendusSectionProps {
  suiteId: string;
  comptesRendus: any[];
  medecinId: string;
  suiteTerminee: boolean;
  onRefresh: () => void;
}

const textareaClass = 'w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors resize-none text-sm bg-white';

function formatContenu(cr: any): string {
  if (cr.contenu && cr.contenu.trim()) return cr.contenu;
  const parts = [cr.etat_patient, cr.mesures_prises, cr.instructions_suivant].filter((p) => p && p.trim());
  return parts.join('\n\n');
}

export default function ComptesRendusSection({
  suiteId,
  comptesRendus,
  medecinId,
  suiteTerminee,
  onRefresh,
}: ComptesRendusSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [contenu, setContenu] = useState('');

  const handleSubmit = async () => {
    if (!contenu.trim()) {
      setErrorMsg('Rédigez le compte rendu de relève avant de l\'enregistrer.');
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    try {
      const { error } = await supabase.from('comptes_rendus_suite').insert({
        suite_soin_id: suiteId,
        medecin_id: medecinId || null,
        contenu: contenu.trim(),
      });
      if (error) throw error;
      setContenu('');
      setShowForm(false);
      onRefresh();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Compte rendu de relève
        </h2>
        <div className="flex items-center gap-2">
          {!suiteTerminee && (
            <button
              onClick={() => { setShowForm(!showForm); setErrorMsg(null); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                showForm ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-clinic-blue text-white hover:bg-clinic-blue-dark'
              }`}
            >
              <i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i>
              {showForm ? 'Annuler' : 'Rédiger un compte rendu'}
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-5">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line flex-shrink-0"></i>{errorMsg}
          </div>
        )}

        {showForm && (
          <div className="p-5 bg-amber-50/50 border border-amber-200 rounded-xl space-y-4">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
              Rédigé avant de quitter, pour permettre au médecin suivant de reprendre la suite de soins.
            </p>
            <textarea
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              className={textareaClass}
              rows={8}
              maxLength={1500}
              placeholder="État du patient, soins administrés, points de vigilance, consignes pour le médecin suivant..."
            />
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-5 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Enregistrer le compte rendu
            </button>
          </div>
        )}

        {comptesRendus.length === 0 && !showForm ? (
          <div className="text-center py-8">
            <i className="ri-file-transfer-line text-4xl text-gray-300 mb-3"></i>
            <p className="text-sm text-gray-500">Aucun compte rendu de relève pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {comptesRendus.map((cr) => (
              <div key={cr.id} className="border border-amber-200 rounded-xl p-4 bg-amber-50/40">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {new Date(cr.created_at).toLocaleDateString('fr-FR')} {new Date(cr.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {cr.medecins && (
                    <span className="text-xs text-gray-500">par Dr {cr.medecins.nom} {cr.medecins.prenom}</span>
                  )}
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{formatContenu(cr)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}